import "@/components/home/leader-list.css";
import { memo } from "react";

/**
 * LeaderList — the receipt-style dotted-leader list: a term set left, a dotted
 * rule running across whatever space is left, a value set against it. The
 * ledger's "Fig. 4" comparison and the clerk post's "Fig. 2" escalation ladder
 * both read this way.
 *
 * Both sites hand-wrote the same dl/div/dt/span/dd tree under two block
 * prefixes, and the sheet carried it twice. Four of the five rules were
 * character-identical — the list's reset, the row's baseline flex, the term's
 * lead type, and the dotted rule down to the pixel of its translateY. The
 * fifth shared six of its declarations. Those are the rule now, written once.
 *
 * The three things that genuinely differed stay per-caller, because each one
 * belongs to exactly one site and flattening them would move pixels. `struck`
 * is per row: the ledger sets a struck value beside the live one, so that row's
 * value becomes a baseline pair rather than one string, and the clerk never
 * passes it. `alignValueEnd` is the clerk's: its actions are long enough to
 * wrap inside the post column and the wrapped lines are set against the leader,
 * which the ledger's short values never need. `wrapOnPhone` is the ledger's
 * phone receipt, where the term takes its own line below the small breakpoint
 * and the value drops a type step — three declarations across three selectors,
 * so it travels as a list-level modifier rather than a class on the value.
 *
 * The last two are orthogonal, not two faces of one variant: what a leader list
 * does when the line runs out of room is a separate question at each site, and
 * answering it the same way for both would be a product decision nobody has
 * made. The rows themselves stay upstream — they are per-caller copy.
 */
export const LeaderList = memo(function LeaderList({
  rows,
  alignValueEnd = false,
  wrapOnPhone = false,
}: {
  rows: readonly { term: string; value: string; struck?: string }[];
  alignValueEnd?: boolean;
  wrapOnPhone?: boolean;
}) {
  return (
    <dl
      className="dw-home__leader-list" data-variant={wrapOnPhone ? "wrap" : undefined}
    data-component="LeaderList" data-location="src/components/home/leader-list.tsx">
      {rows.map((row) => (
        <div key={row.term} className="dw-home__leader-row">
          <dt className="dw-home__leader-term">{row.term}</dt>
          <span className="dw-home__leader-rule" aria-hidden />
          <dd
            className="dw-home__leader-value" data-variant={row.struck ? "struck" : undefined} data-align={alignValueEnd ? "right" : undefined} data-tone="primary"
          >
            {row.struck && <s className="dw-home__leader-struck" data-tone="muted">{row.struck}</s>}
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
});
